import { Add, SmartToy, SupportAgent } from '@mui/icons-material';
import { Fab, useTheme, Zoom } from '@mui/material';
import { EDashboard, EEntityType } from '@sharedInterfaces/globalEnums';
import { isEDashboard } from '@sharedInterfaces/IDashboard';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { CompactChatAgent } from '@src/App/Components/_UnderConstruction/CompactChatAgent';
import { CertificateDialog } from '@src/App/Components/Dialogs/CertificateDialog/CertificateDialog';
import { CompetenceDialog } from '@src/App/Components/Dialogs/CompetenceDialog/CompetenceDialog';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import OpportunityDialog from '@src/App/Components/Dialogs/OpportunityDialog/OpportunityDialog';
import { SkillDialog } from '@src/App/Components/Dialogs/SkillDialog/SkillDialog';
import { entityTypeToIcon } from '@src/helper/tsxHelper';
import { isLoggedIn } from '@store/reducer/clientReducer';
import type { AppState } from '@store/store';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

// Styles für die Komponente - now theme-aware
const getStyles = (theme: any) => ({
    container: {
        position: 'fixed' as const,
        bottom: 24,
        right: 24,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'flex-end',
        gap: 12,
    },
    chatFab: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: theme.shadows[6],
        transition: 'all 0.3s ease-in-out',
    },
    fabTransition: {
        transition: 'all 0.3s ease-in-out',
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
    },
    fabPositions: {
        competence: {
            position: 'absolute' as const,
            bottom: 68,
            right: 0,
            transition: 'background-color 0.5s',
        },
        certificate: {
            position: 'absolute' as const,
            bottom: 135,
            right: 0,
            transition: 'background-color 0.5s',
        },
        opportunity: {
            position: 'absolute' as const,
            bottom: 203,
            right: 0,
            transition: 'background-color 0.5s',
        },
        suggestion: {
            position: 'absolute' as const,
            bottom: 271,
            right: 0,
            transition: 'background-color 0.5s',
        },
    },
});

// Interface für die Fab-Buttons
interface FabButtonProps {
    title: string;
    disabled: boolean;
    style: React.CSSProperties;
    onClick: () => void;
    icon: React.ReactNode;
    showInZoom?: boolean;
    isHover?: boolean;
}

/**
 * Einzelner Fab-Button mit optionalem Zoom-Effekt
 */
const FabButton: React.FC<FabButtonProps> = ({
    title,
    disabled,
    style,
    onClick,
    icon,
    showInZoom = false,
    isHover = true,
}) => {
    const button = (
        <Fab title={title} color="primary" disabled={disabled} style={style} onClick={onClick}>
            {icon}
        </Fab>
    );

    if (showInZoom) {
        return <Zoom in={isHover}>{button}</Zoom>;
    }

    return button;
};

/**
 * HoverFab Komponente - zeigt einen schwebenden Aktionsbutton mit zusätzlichen Optionen bei Hover
 */
export function HoverFab(): JSX.Element {
    const theme = useTheme();
    const styles = getStyles(theme);
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);
    const permissions = useSelector((state: AppState) => state.permissions);
    const offline = useSelector((state: AppState) => state.client.offline);
    const loggedIn = useSelector((state: AppState) => isLoggedIn(state.client.secret));

    // Check current dashboard to hide ChatAgent FAB on Admin Dashboard
    const getCurrentDashboard = (): EDashboard => {
        const currentPath = window.location.pathname;
        if (currentPath.includes('/dashboard/admin')) return EDashboard.ADMIN;
        if (currentPath.includes('/dashboard/manager')) return EDashboard.MANAGER;
        if (currentPath.includes('/dashboard/employee')) return EDashboard.EMPLOYEE;
        const stored = sessionStorage.getItem('preferredDashboard');
        return stored && isEDashboard(stored) ? stored : EDashboard.EMPLOYEE;
    };
    const currentDashboard = getCurrentDashboard();

    const [isHover, setHover] = useState(false);
    let timer: NodeJS.Timeout;

    // Feature flags
    const ENABLE_SKILL_FAB = false;
    const ENABLE_COMPETENCE_FAB = false;
    const ENABLE_CERTIFICATE_FAB = false;
    const ENABLE_OPPORTUNITY_FAB = false;

    // Event-Handler für Mouse-Events
    const handleMouseEnter = useCallback(() => {
        if (offline) return;
        clearTimeout(timer);
        setHover(true);
    }, [offline]);

    const handleMouseLeave = useCallback(() => {
        timer = setTimeout(() => {
            setHover(false);
        }, 750);
    }, []);

    // Dialog-Öffnungsfunktionen
    const [chatDialogOpen, setChatDialogOpen] = useState(() => {
        // Prüfe ob Chat nach Navigation wiederhergestellt werden soll
        const navigationState = localStorage.getItem('t4lo_chat_navigation');
        if (navigationState) {
            try {
                const state = JSON.parse(navigationState);
                const now = Date.now();
                // Wenn TTL noch gültig, öffne Chat automatisch
                return now - state.timestamp < state.ttl;
            } catch {
                return false;
            }
        }
        return false;
    });

    const openChatDialog = useCallback(() => {
        setChatDialogOpen(true);
    }, []);

    const closeChatDialog = useCallback(() => {
        setChatDialogOpen(false);
    }, []);

    const openSkillDialog = useCallback(() => {
        if (!isHover) return;
        return openDialog(<SkillDialog id="SkillDialog" />);
    }, [isHover, openDialog]);

    const openCompetenceDialog = useCallback(() => {
        return openDialog(<CompetenceDialog id="CompetenceDialog" resolve={() => {}} />);
    }, [openDialog]);

    const openCertificateDialog = useCallback(() => {
        return openDialog(<CertificateDialog id="CertificateDialog" resolve={() => {}} />);
    }, [openDialog]);

    const openOpportunityDialog = useCallback(() => {
        return openDialog(<OpportunityDialog id="OpportunityDialog" resolve={() => {}} />);
    }, [openDialog]);

    // const openSuggestionDialog = useCallback(() =>
    // {
    //     return openDialog(
    //         <SuggestionsDialog
    //             id="SuggestionsDialog"
    //             resolve={() => { }}
    //         />
    //     );
    // }, [openDialog]);

    // Berechtigungsprüfungen
    const canAddSkills = checkPermissions('Skills', 'Add', permissions);
    const canAddCompetencies = checkPermissions('Competencies', 'Add', permissions);
    const canAddCertificates = checkPermissions('Certificates', 'Add', permissions);
    const canAddOpportunities = checkPermissions('Opportunities', 'Add', permissions);
    // const canAddSuggestions = checkPermissions('Employees', 'Add', permissions);

    return (
        <div style={styles.container}>
            {/* Chat Fab Button - ausblenden bei Admin Dashboard */}
            {loggedIn && currentDashboard !== EDashboard.ADMIN && (
                <FabButton
                    title={langStrings.layout.openAiChat}
                    disabled={false}
                    style={styles.chatFab}
                    onClick={openChatDialog}
                    icon={<SmartToy />}
                />
            )}
            <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {/* Hauptbutton - ändert Icon bei Hover */}
                {ENABLE_SKILL_FAB && canAddSkills && (
                    <FabButton
                        title={langStrings.layout.createSkill}
                        disabled={offline}
                        style={styles.fabTransition}
                        onClick={openSkillDialog}
                        icon={isHover ? entityTypeToIcon(EEntityType.SKILL) : <Add />}
                    />
                )}

                {/* Zusätzliche Buttons - erscheinen bei Hover */}
                {ENABLE_COMPETENCE_FAB && canAddCompetencies && (
                    <FabButton
                        title={langStrings.layout.createCompetence}
                        disabled={offline}
                        style={styles.fabPositions.competence}
                        onClick={openCompetenceDialog}
                        icon={entityTypeToIcon(EEntityType.COMPETENCE)}
                        showInZoom={true}
                        isHover={isHover}
                    />
                )}

                {ENABLE_CERTIFICATE_FAB && canAddCertificates && (
                    <FabButton
                        title={langStrings.layout.createCertificate}
                        disabled={offline}
                        style={styles.fabPositions.certificate}
                        onClick={openCertificateDialog}
                        icon={entityTypeToIcon(EEntityType.CERTIFICATE)}
                        showInZoom={true}
                        isHover={isHover}
                    />
                )}

                {ENABLE_OPPORTUNITY_FAB && canAddOpportunities && (
                    <FabButton
                        title={langStrings.layout.createOpportunity}
                        disabled={offline}
                        style={styles.fabPositions.opportunity}
                        onClick={openOpportunityDialog}
                        icon={entityTypeToIcon(EEntityType.OPPORTUNITY)}
                        showInZoom={true}
                        isHover={isHover}
                    />
                )}

                {/* {canAddSuggestions && (
                    <FabButton
                        title={langStrings.layout.createSuggestion || "Create Suggestion"}
                        disabled={offline}
                        style={styles.fabPositions.suggestion}
                        onClick={openSuggestionDialog}
                        icon={entityTypeToIcon(EEntityType.EMPLOYEE)}
                        showInZoom={true}
                        isHover={isHover}
                    />
                )} */}
            </div>

            {/* CompactChatAgent */}
            <CompactChatAgent open={chatDialogOpen} onClose={closeChatDialog} />
        </div>
    );
}
